import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from './EvaluationsView.module.css';
import { Col, Row } from "react-bootstrap";
import Langs from "../../app/lang/langs";
import { TeamSelector } from "../../components/teams/TeamSelector";
import { config } from "../../app/utils/config";
import { checkPermission, showErrorToast, showSuccessToast } from "../../app/utils/helpers";
import {
    getEvaluationsReport,
    setEvaluationAddModal,
    setOnEvaluateCollab,
    setOnEvaluateDate,
    setOnEvaluateTeamId,
    setAddResponse,
    setEditResponse,
    setSelectedTypeGeneric,
    setSelectedType,
    getEvaluationsTypes,
    setAllowedToEdit,
    generateAnnualPrimeReport,
    generateAnnualPrimeReportDM
} from "../../components/evaluations/evaluationsSlice";
import ImageButton from "../../components/buttons/ImageButton";
import exportIcon from "../../assets/images/export.png";
import exportIconFocus from "../../assets/images/exportFocus.png";
import ExcelManager from "../../app/managers/excelManager";
import { getCollabs } from "../../components/collabs/collabsSlice";
import moment from "moment";
import { ViewSelectButton } from "../../components/buttons/ViewSelectButton";
import EvaluationsViewManagers from "./EvaluationsViewManagers";
import { useNavigate, useParams } from "react-router-dom";
import AddEvaluationModal from "./modals/AddEvaluationModal";
import EvaluationsToolbar from "./components/EvaluationsToolbar";
import EvaluationsTable from "./components/EvaluationsTable";
import MobileEvaluationsList from "./mobile/MobileEvaluationsList";
import MobileEvaluationsTopMenu from "./mobile/MobileEvaluationsTopMenu";
import MobileAddEvaluationModal from "./mobile/MobileAddEvaluationModal";
import {
    getAllowedTeamIds,
    getTeamMonthData,
    convertReportsToArray,
    processReports,
    summarizeReports
} from "./utils/evaluationUtils";


export default function EvaluationsView() {

    const dispatch = useDispatch();
    const urlParams = useParams();
    const navigate = useNavigate();

    const [viewsOptions] = useState([
        { label: <Langs str='Evaluations Collab' />, value: 0 },
        { label: <Langs str='Evaluation DM' />, value: 1 },
    ]);
    const [selectedView, setSelectedView] = useState(0);

    const selectedTeams = useSelector((state) => state.teams.selectedTeamsIds);
    const reports = useSelector((state) => state.evaluations.report);
    const myTeam = useSelector((state) => state.teams.myTeam);
    const addEvaluationRes = useSelector((state) => state.evaluations.addResponse);
    const editEvaluationRes = useSelector((state) => state.evaluations.editResponse);
    const types = useSelector((state) => state.evaluations.genericTypes);
    const selectedType = useSelector((state) => state.evaluations.selectedTypeGeneric);
    const isMobile = useSelector((state) => state.settings.isMobile);

    const mounted = useRef(false);

    const [expandedTeamId, setExpandedTeamId] = useState(null);
    const [date, setDate] = useState(new Date());
    const [onViewTeam, setOnViewTeam] = useState(null);
    const [selectedTeamId, setSelectedTeamId] = useState();

    // ─── Derived values ───────────────────────────────────────────────────────

    const year = useMemo(() => date.getFullYear(), [date]);

    const allowedTeams = useMemo(
        () => (selectedTeams?.length ? getAllowedTeamIds(selectedTeams) : []),
        [selectedTeams]
    );

    const groupedReports = useMemo(
        () => convertReportsToArray(reports, year),
        [reports, year]
    );

    const processedReports = useMemo(
        () => processReports(groupedReports, year),
        [groupedReports, year]
    );

    const summarizedReport = useMemo(
        () => summarizeReports(processedReports),
        [processedReports]
    );

    const visibleTeams = useMemo(() =>
        allowedTeams.filter(id =>
            !myTeam ||
            checkPermission(config.scopes.evaluations, config.permissionTypes.view, myTeam.id) ||
            myTeam.id !== id
        ),
        [allowedTeams, myTeam]
    );

    const monthColumns = useMemo(() =>
        Array.from({ length: 12 }, (_, index) => ({
            key: index + 1,
            header: (
                <div>
                    <Langs str={new Date(2000, index).toLocaleString('default', { month: 'short' })} />
                    <br />
                    {getTeamMonthData(summarizedReport, onViewTeam, index + 1)}
                </div>
            ),
        })),
        [summarizedReport, onViewTeam]
    );

    const groupedData = useMemo(() => {
        const groups = {};
        groupedReports.forEach(row => {
            if (!groups[row.id_team]) {
                groups[row.id_team] = { team_name: row.team_name, id_team: row.id_team, rows: [] };
            }
            groups[row.id_team].rows.push(row);
        });
        return Object.values(groups);
    }, [groupedReports]);

    // ─── Effects ─────────────────────────────────────────────────────────────

    useEffect(() => {
        mounted.current = true;
    }, []);

    useEffect(() => {
        if (mounted.current && selectedTeams?.length) {
            dispatch(getCollabs({ selectedTeamsIds: selectedTeams }));
        }
    }, [selectedTeams]);

    useEffect(() => {
        if (selectedTeams?.length) {
            dispatch(getEvaluationsTypes({ idTeam: selectedTeams }));
        }
    }, [selectedTeams]);

    useEffect(() => {
        if (selectedView === 0 && year && selectedType && allowedTeams.length) {
            dispatch(getEvaluationsReport({
                idTeams: allowedTeams,
                year,
                idType: selectedType.id,
            }));
        }
    }, [allowedTeams, year, selectedType, selectedView]);

    useEffect(() => {
        if (selectedTeams.length === 1) {
            setOnViewTeam(selectedTeams[0]);
        } else if (selectedTeams.length > 1) {
            setOnViewTeam(null);
        }
    }, [selectedTeams]);

    useEffect(() => {
        if (urlParams.subView && viewsOptions[urlParams.subView]) {
            setSelectedView(parseInt(urlParams.subView));
        }
    }, [urlParams]);

    // ─── Response handlers ────────────────────────────────────────────────────

    const handleApiResponse = useCallback((res, clearAction) => {
        const lower = res.trim().toLowerCase();
        if (lower.includes("successfully")) {
            showSuccessToast("Success!");
        } else if (lower.includes("duplicate")) {
            showErrorToast(<Langs str="DUPICATE_WARNING" />);
        } else {
            showErrorToast(res);
        }
        dispatch(clearAction(null));
    }, [dispatch]);

    const refetchReport = useCallback(() => {
        if (selectedView === 0 && year && selectedType && allowedTeams.length) {
            dispatch(getEvaluationsReport({
                idTeams: allowedTeams,
                year,
                idType: selectedType.id,
            }));
        }
    }, [dispatch, selectedView, year, selectedType, allowedTeams]);

    useEffect(() => {
        if (addEvaluationRes != null) {
            handleApiResponse(addEvaluationRes, setAddResponse);
            if (addEvaluationRes.trim().toLowerCase().includes("successfully")) {
                refetchReport();
            }
        }
    }, [addEvaluationRes]);

    useEffect(() => {
        if (editEvaluationRes != null) {
            handleApiResponse(editEvaluationRes, setEditResponse);
            if (editEvaluationRes.trim().toLowerCase().includes("successfully")) {
                refetchReport();
            }
        }
    }, [editEvaluationRes]);

    // ─── Handlers ─────────────────────────────────────────────────────────────

    const handleTypeChange = useCallback((e) => {
        dispatch(setSelectedTypeGeneric(e.value));
    }, [dispatch]);

    const handleViewChange = (view) => {
        if (view != null) {
            navigate(`/evaluations/${selectedTeams[0]}/${view}`);
        }
    };

    const toggleGroupExpansion = useCallback((teamId) => {
        setExpandedTeamId(prev => {
            const next = prev === teamId ? null : teamId;
            setOnViewTeam(next);
            return next;
        });
    }, []);

    const showModalEditAdd = useCallback((collab, date, entreeDate, sortieDate, id_team, dateStartMonth) => {
        const isAllowed =
            (entreeDate ? date.isAfter(entreeDate) : true) &&
            (sortieDate ? dateStartMonth.isBefore(moment(sortieDate)) : true);
        dispatch(setAllowedToEdit(isAllowed));
        dispatch(setOnEvaluateTeamId(id_team));
        dispatch(setOnEvaluateCollab(collab));
        dispatch(setOnEvaluateDate(date));
        dispatch(setEvaluationAddModal(true));
        dispatch(setSelectedType(selectedType));
    }, [dispatch, selectedType]);

    const exportExcel = useCallback(() => {
        if (selectedView === 0) {
            dispatch(generateAnnualPrimeReport({
                idTeams: [selectedTeams[0]],
                year,
            })).then(actionResponse => {
                if (generateAnnualPrimeReport.fulfilled.match(actionResponse)) {
                    const exportData = actionResponse.payload?.responseData?.annual_report;
                    if (exportData) {
                        ExcelManager.getInstance().GenerateAndDownloadAnnualPrimeReport(exportData, "evaluations reports");
                    }
                }
            });
        } else {
            dispatch(generateAnnualPrimeReportDM({
                idTeams: [selectedTeams[0]],
                year,
            })).then(actionResponse => {
                if (generateAnnualPrimeReportDM.fulfilled.match(actionResponse)) {
                    const exportData = actionResponse.payload?.responseData?.annual_report;
                    if (exportData) {
                        ExcelManager.getInstance().GenerateAndDownloadAnnualPrimeReportDM(exportData, "evaluations DM reports");
                    }
                }
            });
        }
    }, [dispatch, selectedTeams, year, selectedView]);

    // ─── Render ───────────────────────────────────────────────────────────────

    const scrollHeight = isMobile === 1
        ? 'calc(100vh - 250px)'
        : isMobile === 3 ? 'calc(100vh - 165px)' : 'calc(100vh - 350px)';

    if (isMobile === 2) {
        return (
            <div className={styles.containerView} style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
                <MobileEvaluationsTopMenu
                    selectedView={selectedView}
                    onViewChange={handleViewChange}
                    date={date}
                    onDateChange={(val) => setDate(val)}
                    onExport={exportExcel}
                    showExport={selectedTeams.length === 1}
                />
                {selectedView === 0 ? (
                    <MobileEvaluationsList
                        groupedData={groupedData}
                        visibleTeams={visibleTeams}
                        expandedTeamId={expandedTeamId}
                        onToggleGroup={toggleGroupExpansion}
                        year={year}
                        onClickCell={showModalEditAdd}
                    />
                ) : (
                    <EvaluationsViewManagers date={date} onDateChange={setDate} />
                )}
                <MobileAddEvaluationModal selectedDate={date} selectedTeamId={selectedTeamId} />
            </div>
        );
    }

    // Desktop & tablet — original render
    return (
        <div className={styles.containerView}>
            <div className="topMenu" style={{ height: 'auto' }}>
                {isMobile !== 3 ? (
                    <Row>
                        <Col lg={5}>
                            <span className="headerTitle">
                                <Langs str="Evaluations" />
                            </span>
                            {(isMobile === 1 || isMobile === 3) && <TeamSelector includeMyTeam />}
                        </Col>
                        <Col lg={5} className="d-flex justify-content-start pe-4 align-items-center p-0 m-0">
                            <ViewSelectButton
                                options={viewsOptions}
                                selectedView={viewsOptions.find(o => o.value == selectedView)?.value}
                                onViewChange={handleViewChange}
                            />
                        </Col>
                        <Col lg={2} xs={12} className={(isMobile === 1 || isMobile === 3) ? "d-flex justify-content-end pe-4 align-items-center p-0 m-0" : "d-flex justify-content-between px-2 align-items-center p-0 m-0 mt-2"}>
                            {isMobile === 2 && <TeamSelector includeMyTeam />}
                            {selectedTeams.length === 1 &&
                                <ImageButton
                                    image={exportIcon}
                                    imageFocus={exportIconFocus}
                                    onClick={exportExcel}
                                    active={true}
                                    tooltip="Export"
                                    width={32}
                                />
                            }
                        </Col>
                    </Row>
                ) : (
                    <div></div>
                )}
            </div>
            {selectedView === 0 ? (
                <div className={`${styles.collabsCard} ${isMobile === 2 ? styles.mobileBody : ''}`}>
                    <EvaluationsToolbar
                        date={date}
                        onDateChange={(val) => setDate(val)}
                        selectedType={selectedType}
                        onTypeChange={handleTypeChange}
                        types={types}
                        typeDisabled={true}
                        isMobile={isMobile}
                    />
                    <EvaluationsTable
                        scrollHeight={scrollHeight}
                        groupedData={groupedData}
                        monthColumns={monthColumns}
                        visibleTeams={visibleTeams}
                        expandedTeamId={expandedTeamId}
                        onToggleGroup={toggleGroupExpansion}
                        year={year}
                        onClickCell={showModalEditAdd}
                    />
                </div>
            ) : (
                <EvaluationsViewManagers date={date} onDateChange={setDate} />
            )}
            <AddEvaluationModal selectedDate={date} selectedTeamId={selectedTeamId} />
        </div>
    );
}
